/** Account's sub-pages (settings pocket spec §6): Password, Invoices and Delete account. */
import { useEffect, useState } from "react";
import { changePassword, deleteAccount } from "@/api/auth";
import { getInvoices, type Invoice } from "@/api/billing";
import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { clearDictationAnywhereHistory } from "@/desktop/dictation-anywhere/dictation-anywhere-native";
import { useAuth } from "@/hooks/use-auth";
import { formatNumber, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsAccountMessages } from "@/i18n/messages/settings-account";
import { clearOnboardingLocalState } from "@/lib/onboarding-local-state";
import { openExternalUrl } from "@/lib/open-external-url";
import { clearPersistedDictationAnywhereSettings } from "@/stores/dictation-anywhere-settings-store";
import { clearPersistedDictationSettings } from "@/stores/dictation-settings-store";
import { clearPersistedTtsSettings } from "@/stores/tts-settings-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";

/** Stripe invoice statuses, as settings-account catalogue keys. */
const INVOICE_STATUS_KEY = {
  draft: "invoiceStatusDraft",
  open: "invoiceStatusOpen",
  paid: "invoiceStatusPaid",
  uncollectible: "invoiceStatusUncollectible",
  void: "invoiceStatusVoid",
} as const;

function isInvoiceStatus(
  status: string,
): status is keyof typeof INVOICE_STATUS_KEY {
  return Object.prototype.hasOwnProperty.call(INVOICE_STATUS_KEY, status);
}

function firstMessage(
  failure: unknown,
  fallback: string,
  keys: string[],
): string {
  const data = (failure as { data?: Record<string, unknown> } | null)?.data;
  for (const key of keys) {
    const value = data?.[key];
    if (Array.isArray(value) && typeof value[0] === "string")
      return value.join(", ");
    if (typeof value === "string" && value.trim()) return value;
  }
  return fallback;
}

export function AccountPasswordPage() {
  const t = useT(settingsAccountMessages);
  const nav = useSettingsNav();
  const { user } = useAuth();
  // Google sign-ups have no password yet, so there is nothing to confirm.
  const needsCurrent = user?.has_password ?? true;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const incomplete = (needsCurrent && !current) || !next || !confirm;

  async function submit() {
    if (saving || incomplete) return;
    if (next !== confirm) {
      setError(t("passwordsDontMatch"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await changePassword({
        ...(needsCurrent ? { old_password: current } : {}),
        new_password1: next,
        new_password2: confirm,
      });
      nav.back();
    } catch (failure) {
      setError(
        firstMessage(failure, t("changePasswordFailed"), [
          "old_password",
          "new_password1",
          "new_password2",
          "detail",
        ]),
      );
      setSaving(false);
    }
  }

  return (
    <div data-testid="settings-page-account-password">
      {needsCurrent && (
        <SettingsTextField
          label={t("currentPassword")}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          testId="account-password-current"
        />
      )}
      <SettingsTextField
        label={t("newPassword")}
        type="password"
        autoComplete="new-password"
        value={next}
        onChange={(event) => setNext(event.target.value)}
        testId="account-password-new"
      />
      <SettingsTextField
        label={t("confirmNewPassword")}
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        testId="account-password-confirm"
      />
      <SettingsFieldError error={error} testId="account-password-error" />
      <div className="mt-3 px-2">
        <Button
          size="sm"
          disabled={saving || incomplete}
          onClick={() => void submit()}
          data-testid="account-password-save"
        >
          {saving ? t("changingPassword") : t("changePassword")}
        </Button>
      </div>
    </div>
  );
}

export function AccountInvoicesPage() {
  const t = useT(settingsAccountMessages);
  const common = useT(commonMessages);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    getInvoices(50).then(
      (loaded) => {
        if (active) setInvoices(loaded);
      },
      () => {
        if (active) setFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [attempt]);

  if (failed)
    return (
      <PocketStatement
        size="panel"
        title={t("invoicesLoadFailed")}
        action={
          <Button
            size="sm"
            onClick={() => setAttempt((n) => n + 1)}
            data-testid="account-invoices-retry"
          >
            {common("retry")}
          </Button>
        }
      />
    );
  if (invoices === null) {
    return (
      <p
        className="px-2 py-3 text-appkit-small font-medium text-muted"
        data-testid="settings-page-account-invoices"
      >
        {common("loading")}
      </p>
    );
  }
  return (
    <PocketSection testId="settings-page-account-invoices">
      {invoices.length === 0 ? (
        <p className="px-2 py-3 text-appkit-small text-muted">
          {t("noInvoices")}
        </p>
      ) : null}
      {invoices.map((invoice) => {
        const url = invoice.hosted_invoice_url;
        return (
          <PocketRow
            key={invoice.id}
            testId={`settings-row-invoice-${invoice.id}`}
            title={invoice.date}
            secondLine={t("invoiceSummary", {
              amount: formatNumber(invoice.amount_cents / 100, {
                style: "currency",
                currency: "EUR",
              }),
              status: isInvoiceStatus(invoice.status)
                ? t(INVOICE_STATUS_KEY[invoice.status])
                : invoice.status,
            })}
            trailing={
              url ? (
                <Icons.ExternalLink
                  className="h-3 w-3 text-subtle"
                  aria-hidden="true"
                />
              ) : undefined
            }
            onSelect={url ? () => void openExternalUrl(url) : undefined}
          />
        );
      })}
    </PocketSection>
  );
}

export function AccountDeletePage() {
  const t = useT(settingsAccountMessages);
  const { logout } = useAuth();
  const [phrase, setPhrase] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deletePhrase = t("deletePhrase");
  // Any case; the English phrase is accepted in every language.
  const typed = phrase.trim().toLocaleLowerCase();
  const confirmed = [
    deletePhrase,
    settingsAccountMessages.en.deletePhrase,
  ].some((accepted) => accepted.toLocaleLowerCase() === typed);

  async function remove() {
    if (!confirmed || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAccount();
    } catch (failure) {
      setError(firstMessage(failure, t("deleteFailed"), ["error"]));
      setDeleting(false);
      return;
    }
    // The identity is gone: wipe user-flavored local state that plain
    // logout keeps for a returning user, then run the standard teardown.
    // Account deletion already ended the server session.
    clearOnboardingLocalState("all");
    clearPersistedTtsSettings();
    clearPersistedDictationSettings();
    clearPersistedDictationAnywhereSettings();
    // The teardown wipes the last dictations too; doing it here means the
    // wipe does not depend on how far that teardown gets. Best-effort, so a
    // failure cannot leave a deleted account signed in.
    await clearDictationAnywhereHistory().catch(() => {});
    await logout({ skipServer: true });
  }

  return (
    <div data-testid="settings-page-account-delete">
      <PocketStatement
        size="panel"
        title={t("deleteTitle")}
        line={t("deleteLine")}
      />
      <SettingsTextField
        label={t("deleteConfirmLabel", { phrase: deletePhrase })}
        autoComplete="off"
        value={phrase}
        onChange={(event) => setPhrase(event.target.value)}
        testId="account-delete-confirm"
      />
      <SettingsFieldError error={error} testId="account-delete-error" />
      <div className="mt-3 px-2">
        <Button
          variant="destructive"
          size="sm"
          disabled={!confirmed || deleting}
          onClick={() => void remove()}
          data-testid="account-delete-submit"
        >
          {deleting ? t("deleting") : t("deletePermanently")}
        </Button>
      </div>
    </div>
  );
}
